import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  LicenceLexiClient,
  MessagingLexiClient,
  PartenaireDto,
  PartenairesLexiClient,
  Permissions,
  SecurityLexiClient, SyncDataLexiClient,
  TypeUtilisateur, UtilisateurRelationLexiClient
} from '@lexi-clients/lexi';
import { LexiUser } from '@lexi/oidc-uaa';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { DxSelectBoxTypes } from "devextreme-angular/ui/select-box";
import { AppInitService } from 'lexi-angular/src/app/app-init.service';
import { Site } from 'lexi-angular/src/app/domain/site';
import { Societe } from 'lexi-angular/src/app/domain/societe';
import { DynamicNavigationMenuService } from 'lexi-angular/src/app/services/dynamic-navigation-menu.service';
import { SocieteService } from 'lexi-angular/src/app/services/societe.service';
import { LexiStorage } from 'lexi-angular/src/app/settings/lexi-storage';
import { filter, Subject, takeUntil } from 'rxjs';
import { navigationCollaborateur, navigationPartenaire, NavigationTop, Parametrage } from '../../../models/navigation-top';
import { AuthService, STORE_KEY_RETURN_URL } from '../../../settings/auth.service';
import { AppInfoService } from '../../services';
import { DxDataSourceService } from '../../services/dx-data-source.service';
import { FeatureFlagService } from "../../services/feature-flag.service";
import { ModuleService } from '../../services/module.service';

@Component({
    template: 'no-need-ui--but-we-specified-fake-one-because-of-compiler',
    standalone: false
})
export class HeaderBaseComponent implements OnInit, OnDestroy {
  @Output()
  menuToggle = new EventEmitter<boolean>();
  @ViewChild('societeSelectBox', { static: false }) societeIdSelectBox: DxSelectBoxComponent;

  options: any[];
  _currentModuleId: number = 0;
  get currentModuleId() {
    return this._currentModuleId;
  }

  // Cet array ne doit pas être réinitialisé car
  // celui-ci doit garder une adresse fixe dans la mémoire
  memoireSocietes: Societe[] = [];

  @Input()
  menuToggleEnabled = false;

  @Input()
  title: string;

  get user(): LexiUser {
    return this.authService.authenticatedUser;
  }

  get societes(): Societe[] {
    // Reset l'array memoireSocietes sans le réinitialisé
    this.memoireSocietes.splice(0);
    // Push les sociétés filtrées dans l'array en mémoire
    this.authService.societes.filter(societe => societe.enabled == true).forEach(societe => {
      this.memoireSocietes.push(societe);
    });
    return this.memoireSocietes;
  }

  currentSociete: Societe;
  currentClient: PartenaireDto;

  navPrincipal: Array<NavigationTop> = [];
  currentSite: Site;
  currentSitePartenaire: PartenaireDto;

  // Subject pour gérer les désabonnements
  private readonly destroy$ = new Subject<void>();

  constructor(
    protected readonly router: Router,
    protected readonly authService: AuthService,
    protected readonly moduleService: ModuleService,
    protected readonly partenairesLexiClient: PartenairesLexiClient,
    protected readonly messagingClient: MessagingLexiClient,
    protected readonly syncDataService: SyncDataLexiClient,
    protected readonly userRelationService: UtilisateurRelationLexiClient,
    private readonly dynamicNavigationMenuService: DynamicNavigationMenuService,
    protected readonly licencesClient: LicenceLexiClient,
    protected readonly securityHttpClient: SecurityLexiClient,
    private readonly storage: LexiStorage,
    private readonly customSocieteService: SocieteService,
    private readonly dxDataSourceService: DxDataSourceService,
    protected readonly featureFlagService: FeatureFlagService,
    protected appInitService: AppInitService,
    protected appInfo: AppInfoService,
  ) {
    // Configuration des souscriptions pour suivre les changements d'état de l'application
    this.setupStateSubscriptions();
  }

  /**
   * Configure toutes les souscriptions aux états de l'application
   * avec gestion des désabonnements pour éviter les fuites mémoire
   */
  private setupStateSubscriptions(): void {
    // Souscription au changement de site
    this.authService.currentSiteId$.pipe(
      filter((siteId: number) => siteId != null),
      takeUntil(this.destroy$)
    ).subscribe((siteId: number) => {
      this.currentSite = this.authService.currentSite;
      // Récupération des données du partenaire associé au site
      this.dxDataSourceService.getPartenaireById(this.authService.currentSite?.partenaireId)
        .then(partenaire => this.currentSitePartenaire = partenaire);
    });

    // Souscription au changement de module actif
    this.moduleService.currentModule.asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(moduleId => this._currentModuleId = moduleId);

    // Souscription au changement de société
    this.authService.currentSocieteId$.pipe(
      filter((societeId: number) => societeId != null),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentSociete = this.authService.currentSociete;
    });

    // Souscription aux changements d'utilisateur
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async user => this.handleUserChange());
  }

  /**
   * Gère les changements d'utilisateur et met à jour l'interface en conséquence
   */
  private async handleUserChange(): Promise<void> {
    const userIsCollaborateur: boolean = this.authService.securityUser?.typeUtilisateur ==
      TypeUtilisateur.collaborateur.toLowerCase();

    // Redirection au portail client pour les partenaires
    if (!userIsCollaborateur) {
      this.navigate(0);
      return;
    }

    // Configuration du menu de navigation principal
    await this.setNavPrincipal(userIsCollaborateur);

    // Création des options de menu avec style et handlers
    this.options = this.navPrincipal.map((item) => ({
      text: item.title,
      visible: item.visible,
      stylingMode: "text",
      onClick: () => this.navigate(item.id)
    }));

    // Détermination de la destination initiale de navigation
    this.navigateToInitialDestination();
  }

  /**
   * Détermine et navigue vers la destination initiale appropriée
   * basée sur l'URL actuelle ou le dernier URL stocké
   */
  private navigateToInitialDestination(): void {
    const currentUrl = this.router.url;
    const moduleName = currentUrl.split('/')[1];

    // Recherche du module correspondant à l'URL actuelle
    const module = this.navPrincipal.find(
      item => item.title.toLocaleLowerCase() === moduleName.toLocaleLowerCase()
    );

    // Premier module disponible comme solution de repli
    const firstNavEnabled = this.navPrincipal.find(item => item.visible);

    // Priorité: module actuel > module stocké > premier module disponible
    const targetNav: NavigationTop = module || this.getNavigationMenuByStoredUrl();

    if (targetNav || firstNavEnabled) {
      this.navigate(targetNav?.id || firstNavEnabled.id);
    }
  }

  private async setNavPrincipal(userIsCollaborateur: boolean) {
    if (userIsCollaborateur) {
      const parametrage: Parametrage = {
        currentUserType: await this.authService.getStoredUserTypeRuunui(),
        currentUserCanGererTransporteurs: this.authService.securityUserisGrantedWith(Permissions.canGererTransporteurs),
        currentSocieteManageStock: this.authService.currentSociete != null && this.authService.currentSociete.gestionStock
      };
      const navCollaborateur: NavigationTop[] = this.moduleService.enableModulesActifs(await navigationCollaborateur(parametrage, this.featureFlagService, this.dynamicNavigationMenuService));
      this.navPrincipal = navCollaborateur;
    }

    else {
      let societe = this.customSocieteService.getSocietePortailClient();
      if (!societe) {
        await this.customSocieteService.setSocietePortailClient();
        societe = this.customSocieteService.getSocietePortailClient();
      }
      this.navPrincipal = navigationPartenaire(societe);
    }
  }

  getNavigationMenuByStoredUrl(): NavigationTop {
    const storedUrl = this.storage.getItem(STORE_KEY_RETURN_URL);
    if (!storedUrl) {
      return null;
    }
    const moduleName = storedUrl.split('/')[1];
    return this.navPrincipal.find(f => f.title.toLowerCase() == moduleName.toLowerCase());
  }

  changerModule(event: DxSelectBoxTypes.ValueChangedEvent) {
    this.navigate(event.value);
  }

  navigate(moduleId: number) {
    this.moduleService.selectModule = moduleId;
  }

  ngOnInit() {
    this.authService.refreshAvailableSocietesFromServer(this.authService.securityUser?.id);
  }

  ngOnDestroy(): void {
    // Déclenchement du Subject pour se désabonner de tous les observables
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMenu = () => {
    this.menuToggle.emit();
  }
}
